import { Observable, Subject, distinctUntilChanged, filter, map } from "rxjs";
import { IAccess } from "@webapp/sessions/models/sessions.model";
import { ShareableFilter } from "@webapp/shared/unified-filters/models/shareable-filters.models";

type OkrViewEvent = {
  reason: OkrViewChangeEventReason;
  data: Partial<ShareableFilter> | null;
};

type OkrViewChangeEventReason = "update" | "notify";

export class OkrViewEventsManager {
  private okrViewId: string;
  private static okrViewChangeSubjectMap = new Map<string, Subject<OkrViewEvent>>();

  constructor(okrViewId: string) {
    this.okrViewId = okrViewId;
  }

  public notifyOkrViewAccessChanged(access: IAccess): void {
    this.ensureOkrViewChangeSubjectReady(this.okrViewId);

    this.getOkrViewChangeSubject(this.okrViewId).next({ data: { access }, reason: "update" });
  }

  public okrViewAccessChanged$(): Observable<ShareableFilter["access"]> {
    this.ensureOkrViewChangeSubjectReady(this.okrViewId);

    return this.getOkrViewChangeSubject(this.okrViewId).pipe(
      filter((okrViewChangesObj) => okrViewChangesObj.reason === "update" && !!okrViewChangesObj.data.access),
      map((okrViewChangesObj) => okrViewChangesObj.data.access),
      distinctUntilChanged()
    );
  }

  public notifyOkrViewFilterChanged(update: Pick<ShareableFilter, "filters" | "sorting">): void {
    this.ensureOkrViewChangeSubjectReady(this.okrViewId);

    this.getOkrViewChangeSubject(this.okrViewId).next({ data: { ...update }, reason: "update" });
  }

  public okrViewFilterChanged$(): Observable<Pick<ShareableFilter, "filters" | "sorting">> {
    this.ensureOkrViewChangeSubjectReady(this.okrViewId);

    return this.getOkrViewChangeSubject(this.okrViewId).pipe(
      filter((okrViewChangesObj) => okrViewChangesObj.reason === "update" && (!!okrViewChangesObj.data.filters || !!okrViewChangesObj.data.sorting)),
      map((okrViewChangesObj) => ({
        filters: okrViewChangesObj.data.filters,
        sorting: okrViewChangesObj.data.sorting,
      })),
      distinctUntilChanged()
    );
  }

  public notifyOkrViewPrivateAccessChangeRequested(): void {
    this.ensureOkrViewChangeSubjectReady(this.okrViewId);

    this.getOkrViewChangeSubject(this.okrViewId).next({ data: null, reason: "notify" });
  }

  public okrViewPrivateAccessChangeRequested$(): Observable<void> {
    this.ensureOkrViewChangeSubjectReady(this.okrViewId);

    return this.getOkrViewChangeSubject(this.okrViewId).pipe(
      filter((okrViewChangesObj) => okrViewChangesObj.reason === "notify"),
      map(() => null)
    );
  }

  private ensureOkrViewChangeSubjectReady(okrViewId: string): void {
    if (!this.checkIfOkrViewChangeSubjectExists(okrViewId)) {
      this.registerNewOkrViewChangeSubject(okrViewId);
    }
  }

  private checkIfOkrViewChangeSubjectExists(okrViewId: string): boolean {
    return OkrViewEventsManager.okrViewChangeSubjectMap.has(okrViewId);
  }

  private registerNewOkrViewChangeSubject(okrViewId: string): void {
    OkrViewEventsManager.okrViewChangeSubjectMap.set(okrViewId, new Subject<OkrViewEvent>());
  }

  private getOkrViewChangeSubject(okrViewId: string): Subject<OkrViewEvent> {
    return OkrViewEventsManager.okrViewChangeSubjectMap.get(okrViewId);
  }
}
